'use client';

import { useEffect, useRef, useState } from 'react';

import { useTranslations } from 'next-intl';

import { useAuthModal } from '@/app/(main)/components/AuthModalProvider';
import { SceneCanvas } from '@/app/(main)/components/SceneCanvas';
import {
	CALC,
	PLANS,
	RATES,
	computeEstimate,
	formatUsd,
	nf,
	type PlanId,
	type SmsRoute,
} from '@/lib/pricing/data';

// ─── Toggle-state badge (ON/OFF) used inside inverted panels ──────────────────
function StateTag({
	on,
	labelOn,
	labelOff,
}: {
	on: boolean;
	labelOn: string;
	labelOff: string;
}) {
	return (
		<span
			className={`shrink-0 border px-[10px] py-[5px] font-mono text-[10px] tracking-[1px] ${
				on
					? 'bg-bg text-fg border-bg'
					: 'border-bg/25 text-bg/72 bg-transparent'
			}`}
		>
			{on ? labelOn : labelOff}
		</span>
	);
}

// ─── Main component ───────────────────────────────────────────────────────────
export function PricingCalculator() {
	const t = useTranslations('PricingPage');
	const { open: openAuth } = useAuthModal();

	// ── State — mirrors design's initial state exactly ──
	const [plan, setPlan] = useState<PlanId>('compliance');
	const [applicants, setApplicants] = useState(3000);
	const [kyb, setKyb] = useState(false);
	const [companies, setCompanies] = useState(200);
	const [whiteLabel, setWhiteLabel] = useState(false);
	const [extSupport, setExtSupport] = useState(false);
	const [smsOtp, setSmsOtp] = useState(false);
	const [smsRoute, setSmsRoute] = useState<SmsRoute>('global');
	const [annual, setAnnual] = useState(false);
	const [mobile, setMobile] = useState(false);

	const rootRef = useRef<HTMLDivElement>(null);

	// ResizeObserver → mobile flag (<760px, matching design's calcCols breakpoint)
	useEffect(() => {
		const el = rootRef.current;
		if (!el) return;
		const measure = () => setMobile(el.clientWidth < 760);
		measure();
		const ro = new ResizeObserver(measure);
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	// ── Compute estimate (verbatim port of design's renderVals()) ──────────────
	const estimate = computeEstimate({
		plan,
		applicants,
		kyb,
		companies,
		whiteLabel,
		extSupport,
		smsOtp,
		smsRoute,
		annual,
	});

	const {
		isEnt,
		monthly,
		annualMonthly,
		annualSave,
		oneTime,
		quota,
		burnN,
		overN,
		usedCells,
		minApplied,
		kybMinApplied,
		planPart,
		kybPart,
		smsPart,
	} = estimate;

	// Active rate (enterprise falls back to start for quota display)
	const R = plan !== 'enterprise' ? RATES[plan] : RATES.start;
	const NCELL = CALC.burnMeter.cells; // 24

	// ── Translated labels pre-computed for type safety ─────────────────────────
	const stateOn = t('calc.stateOn');
	const stateOff = t('calc.stateOff');

	const planNames: Record<PlanId, string> = {
		start: t('plans.start.name'),
		compliance: t('plans.compliance.name'),
		enterprise: t('plans.enterprise.name'),
	};
	const planDescs: Record<PlanId, string> = {
		start: t('plans.start.desc'),
		compliance: t('plans.compliance.desc'),
		enterprise: t('plans.enterprise.desc'),
	};
	const planPriceUnits: Record<PlanId, string> = {
		start: t('plans.start.priceUnit'),
		compliance: t('plans.compliance.priceUnit'),
		enterprise: t('plans.enterprise.priceUnit'),
	};
	const planApplicants: Record<PlanId, string> = {
		start: t('plans.start.applicant'),
		compliance: t('plans.compliance.applicant'),
		enterprise: t('plans.enterprise.applicant'),
	};
	const planIncluded: Record<PlanId, string> = {
		start: t('plans.start.included'),
		compliance: t('plans.compliance.included'),
		enterprise: t('plans.enterprise.included'),
	};
	const tabLabels: Record<PlanId, string> = {
		start: t('calc.tabs.start'),
		compliance: t('calc.tabs.compliance'),
		enterprise: t('calc.tabs.enterprise'),
	};
	const smsRouteLabels: Record<SmsRoute, string> = {
		ru: t('calc.smsRoutes.ru'),
		global: t('calc.smsRoutes.global'),
	};

	// ── Burn-meter note ────────────────────────────────────────────────────────
	let meterNote = '';
	if (!isEnt) {
		if (applicants < quota) {
			meterNote = t('calc.meter.noteBelow', {
				used: nf(Math.min(applicants, quota)),
				quota: nf(quota),
				count: burnN,
			});
		} else if (applicants === quota) {
			meterNote = t('calc.meter.noteExact');
		} else {
			meterNote = t('calc.meter.noteOver', { count: overN, rate: R.rate });
		}
	}

	// ── Invoice rows (mirrors design's renderVals() invoice array) ─────────────
	interface InvoiceRow {
		label: string;
		sub: string;
		val: string;
	}
	const invoiceRows: InvoiceRow[] = [];

	if (isEnt) {
		invoiceRows.push({
			label: t('calc.invoice.enterprisePlan'),
			sub: t('calc.invoice.enterpriseSub'),
			val: t('calc.invoice.enterpriseVal'),
		});
		if (kyb) {
			invoiceRows.push({
				label: t('calc.invoice.kybLabel'),
				sub: t('calc.invoice.kybEntSub'),
				val: t('calc.invoice.kybEntVal'),
			});
		}
		invoiceRows.push({
			label: t('calc.invoice.supportLabel'),
			sub: t('calc.invoice.supportSub'),
			val: t('calc.invoice.supportVal'),
		});
	} else {
		invoiceRows.push({
			label: planNames[plan],
			sub: minApplied
				? t('calc.invoice.minSub', { min: R.min })
				: t('calc.invoice.usageSub', { count: nf(applicants), rate: R.rate }),
			val: formatUsd(planPart),
		});
		if (kyb) {
			invoiceRows.push({
				label: t('calc.invoice.kybLabel'),
				sub: kybMinApplied
					? t('calc.invoice.kybMinSub')
					: t('calc.invoice.kybUsageSub', { count: nf(companies) }),
				val: formatUsd(kybPart),
			});
		}
		if (smsOtp) {
			invoiceRows.push({
				label: t('calc.invoice.smsLabel'),
				sub:
					smsRoute === 'ru'
						? t('calc.invoice.smsRuSub', { count: nf(applicants) })
						: t('calc.invoice.smsGlobalSub', { count: nf(applicants) }),
				val: formatUsd(smsPart),
			});
		}
		if (whiteLabel) {
			invoiceRows.push({
				label: t('calc.invoice.wlLabel'),
				sub: t('calc.invoice.wlSub'),
				val: formatUsd(CALC.addons.whiteLabel.monthly),
			});
		}
		if (extSupport) {
			invoiceRows.push({
				label: t('calc.invoice.esLabel'),
				sub: t('calc.invoice.esSub'),
				val: formatUsd(CALC.addons.extSupport.monthly),
			});
		}
	}

	// ── Estimate panel computed values ─────────────────────────────────────────
	const estPlanCode = isEnt
		? 'ENTERPRISE'
		: plan === 'start'
			? 'START KYC'
			: 'COMPLIANCE KYC';
	const totalCaption = isEnt
		? t('calc.estimate.totalEnterprise')
		: annual
			? t('calc.estimate.totalAnnual')
			: t('calc.estimate.totalMonthly');
	const totalValue = isEnt
		? t('calc.invoice.enterpriseVal')
		: formatUsd(monthly);
	const hasAnnual = !isEnt && annual && monthly > 0;
	const hasOneTime = oneTime > 0;
	const ctaText = isEnt
		? t('calc.estimate.ctaEnterprise')
		: t('calc.estimate.ctaKyc');

	// ── Add-on definitions ─────────────────────────────────────────────────────
	const addonDefs = [
		{
			key: 'whiteLabel' as const,
			label: t('calc.addonToggles.whiteLabel.label'),
			note: t('calc.addonToggles.whiteLabel.note'),
			value: whiteLabel,
			toggle: () => setWhiteLabel((v) => !v),
		},
		{
			key: 'extSupport' as const,
			label: t('calc.addonToggles.extSupport.label'),
			note: t('calc.addonToggles.extSupport.note'),
			value: extSupport,
			toggle: () => setExtSupport((v) => !v),
		},
		{
			key: 'smsOtp' as const,
			label: t('calc.addonToggles.smsOtp.label'),
			note: t('calc.addonToggles.smsOtp.note'),
			value: smsOtp,
			toggle: () => setSmsOtp((v) => !v),
		},
	];

	// ─────────────────────────────────────────────────────────────────────────
	return (
		<div ref={rootRef}>
			{/* Keyframes for burn-meter cells (pc- prefix avoids collisions) */}
			<style>{`
        @keyframes pc-burn {
          from { background-position: 0 0; }
          to   { background-position: 20px 0; }
        }
        @keyframes pc-emberpulse {
          0%, 100% { opacity: .55; }
          50%       { opacity: 1;   }
        }
      `}</style>

			{/* ══ SYS.01 — Plan cards ════════════════════════════════════════════ */}
			<div className="text-muted mt-[30px] font-mono text-[11px] tracking-[1.5px]">
				{t('sys01.label')}
			</div>

			<div
				className="mt-4 grid gap-[14px]"
				style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}
			>
				{PLANS.map((p) => {
					const selected = p.id === plan;
					return selected ? (
						// Selected card — inverted (bg-fg / text-bg)
						<button
							key={p.id}
							type="button"
							onClick={() => setPlan(p.id)}
							className="bg-fg text-bg border-fg flex min-h-[290px] w-full cursor-pointer flex-col border p-[20px] pb-0 text-left font-sans"
						>
							<div className="flex w-full items-center justify-between font-mono text-[10.5px] tracking-[1.5px]">
								<span className="text-bg/72">{p.code}</span>
								<span className="border-bg/30 text-bg/80 border px-[8px] py-[3px] tracking-[1.5px]">
									{p.badge}
								</span>
							</div>
							<div className="mt-[18px] text-[23px] font-medium tracking-[-0.015em]">
								{planNames[p.id]}
							</div>
							<p className="text-bg/72 mt-[10px] min-h-[76px] text-[13px] leading-[1.5]">
								{planDescs[p.id]}
							</p>
							<div className="border-bg/16 mt-[8px] w-full border-t pt-[16px]">
								<div className="flex items-baseline gap-[8px]">
									<span className="font-mono text-[26px] font-bold tracking-[-0.01em]">
										{p.price}
									</span>
									<span className="text-bg/72 font-mono text-[11px]">
										{planPriceUnits[p.id]}
									</span>
								</div>
								<div className="text-bg/72 mt-[8px] font-mono text-[11.5px] tracking-[0.3px]">
									{planApplicants[p.id]}
								</div>
								<div className="text-bg/72 mt-[6px] font-mono text-[10.5px] tracking-[0.3px]">
									{planIncluded[p.id]}
								</div>
							</div>
							<div className="border-bg/16 text-bg mx-[-20px] mt-[18px] flex w-[calc(100%+40px)] justify-between border-t px-[20px] py-[13px] font-mono text-[11px] tracking-[1.5px]">
								{t('planSelected')}
							</div>
						</button>
					) : (
						// Unselected card — surface (bg-bg / text-fg)
						<button
							key={p.id}
							type="button"
							onClick={() => setPlan(p.id)}
							className="bg-bg text-fg border-border hover:border-fg flex min-h-[290px] w-full cursor-pointer flex-col border p-[20px] pb-0 text-left font-sans transition-colors"
						>
							<div className="flex w-full items-center justify-between font-mono text-[10.5px] tracking-[1.5px]">
								<span className="text-muted">{p.code}</span>
								<span className="border-border text-accent border px-[8px] py-[3px] tracking-[1.5px]">
									{p.badge}
								</span>
							</div>
							<div className="mt-[18px] text-[23px] font-medium tracking-[-0.015em]">
								{planNames[p.id]}
							</div>
							<p className="text-accent mt-[10px] min-h-[76px] text-[13px] leading-[1.5]">
								{planDescs[p.id]}
							</p>
							<div className="border-border mt-[8px] w-full border-t pt-[16px]">
								<div className="flex items-baseline gap-[8px]">
									<span className="font-mono text-[26px] font-bold tracking-[-0.01em]">
										{p.price}
									</span>
									<span className="text-accent font-mono text-[11px]">
										{planPriceUnits[p.id]}
									</span>
								</div>
								<div className="text-accent mt-[8px] font-mono text-[11.5px] tracking-[0.3px]">
									{planApplicants[p.id]}
								</div>
								<div className="text-muted mt-[6px] font-mono text-[10.5px] tracking-[0.3px]">
									{planIncluded[p.id]}
								</div>
							</div>
							<div className="border-border text-muted mx-[-20px] mt-[18px] flex w-[calc(100%+40px)] justify-between border-t px-[20px] py-[13px] font-mono text-[11px] tracking-[1.5px]">
								{t('planSelect')}
							</div>
						</button>
					);
				})}
			</div>

			{/* ══ SYS.02 — Calculator ════════════════════════════════════════════ */}
			<div className="text-muted mt-[48px] font-mono text-[11px] tracking-[1.5px]">
				{t('sys02.label')}
			</div>

			{/* Two-column grid (stacks to single column on mobile) */}
			<div
				className="bg-fg border-fg mt-4 grid border"
				style={{
					gridTemplateColumns: mobile
						? 'minmax(0, 1fr)'
						: 'minmax(0, 1fr) minmax(0, 1.05fr)',
					gap: '2px',
				}}
			>
				{/* ── Config panel (inverted) ───────────────────────────────── */}
				<div className="bg-fg text-bg p-[clamp(22px,2.6vw,32px)]">
					{/* Section label */}
					<div className="text-bg/72 font-mono text-[10.5px] tracking-[2px]">
						{t('calc.configHeader')}
					</div>

					{/* Plan tabs */}
					<div className="mt-[18px] flex gap-[6px]">
						{(['start', 'compliance', 'enterprise'] as const).map((id) => (
							<button
								key={id}
								type="button"
								onClick={() => setPlan(id)}
								className={`flex min-h-11 flex-1 cursor-pointer items-center justify-center px-[6px] py-[10px] font-mono text-[10.5px] tracking-[1px] transition-colors ${
									plan === id
										? 'bg-bg text-fg border-bg border'
										: 'border-bg/20 text-bg/65 border bg-transparent'
								}`}
							>
								{tabLabels[id]}
							</button>
						))}
					</div>

					{/* Applicants slider */}
					<div className="mt-[26px]">
						<div className="flex items-baseline justify-between">
							<span className="text-bg/70 font-mono text-[11px] tracking-[1px]">
								{t('calc.applicantsLabel')}
							</span>
							<span className="font-mono text-[18px] font-bold">
								{nf(applicants)}
							</span>
						</div>
						<input
							type="range"
							aria-label={t('calc.applicantsLabel')}
							min={CALC.applicants.min}
							max={CALC.applicants.max}
							step={CALC.applicants.step}
							value={applicants}
							onChange={(e) => setApplicants(parseInt(e.target.value, 10) || 0)}
							className="mt-[12px] min-h-6 w-full cursor-pointer"
							style={{ accentColor: 'var(--color-bg)' }}
						/>
						<div className="text-bg/72 mt-[6px] flex justify-between font-mono text-[9.5px]">
							<span>{t('calc.sliderMin')}</span>
							<span>{t('calc.sliderMax')}</span>
						</div>

						{/* Presets */}
						<div className="mt-[12px] flex flex-wrap gap-[6px]">
							{CALC.presets.map((v) => (
								<button
									key={v}
									type="button"
									onClick={() => setApplicants(v)}
									className="border-bg/20 text-bg/70 hover:border-bg hover:text-bg inline-flex min-h-11 cursor-pointer items-center justify-center border bg-transparent px-[11px] py-[6px] font-mono text-[10px] tracking-[0.5px] transition-colors"
								>
									{v >= 1000 ? `${v / 1000}K` : `${v}`}
								</button>
							))}
						</div>

						{/* Burn meter (non-enterprise only) */}
						{!isEnt && (
							<div className="mt-[20px]">
								<div className="text-bg/72 flex items-baseline justify-between gap-[10px] font-mono text-[9.5px] tracking-[1px]">
									<span>{t('calc.meter.included')}</span>
									<span>
										{nf(Math.min(applicants, quota))} / {nf(quota)}
									</span>
								</div>

								{/* 24-cell meter */}
								<div className="mt-[10px] flex h-[16px] gap-[2px]">
									{Array.from({ length: NCELL }, (_, i) =>
										i < usedCells ? (
											<span key={i} className="bg-bg flex-1" />
										) : (
											<span
												key={i}
												style={{
													flex: '1 1 0',
													backgroundColor:
														'color-mix(in srgb, var(--color-bg) 8%, transparent)',
													backgroundImage:
														'repeating-linear-gradient(45deg,color-mix(in srgb, var(--color-bg) 35%, transparent) 0 3px,transparent 3px 7px)',
													animation:
														'pc-burn 1.1s linear infinite, pc-emberpulse 1.8s ease-in-out infinite',
												}}
											/>
										),
									)}
								</div>

								{/* Legend */}
								<div className="text-bg/72 mt-[9px] flex gap-[16px] font-mono text-[9px] tracking-[1px]">
									<span className="flex items-center gap-[5px]">
										<span className="bg-bg h-[9px] w-[9px]" />
										{t('calc.meter.used')}
									</span>
									<span className="flex items-center gap-[5px]">
										<span
											className="h-[9px] w-[9px]"
											style={{
												backgroundImage:
													'repeating-linear-gradient(45deg,color-mix(in srgb, var(--color-bg) 35%, transparent) 0 2px,transparent 2px 4px)',
												backgroundColor:
													'color-mix(in srgb, var(--color-bg) 8%, transparent)',
											}}
										/>
										{t('calc.meter.burns')}
									</span>
								</div>

								{/* Meter note */}
								<div className="text-bg mt-[12px] font-mono text-[11px] leading-[1.5] tracking-[0.3px]">
									{meterNote}
								</div>

								{/* Min note — border-left callout */}
								<div className="border-bg text-bg/72 mt-[12px] border-l-2 py-[9px] pl-[13px] font-mono text-[10.5px] leading-[1.6]">
									{t('calc.meter.minNote', { min: formatUsd(R.min) })}
								</div>
							</div>
						)}
					</div>

					{/* KYB toggle */}
					<div className="border-bg/12 mt-[22px] border-t pt-[18px]">
						<button
							type="button"
							onClick={() => setKyb((v) => !v)}
							className="text-bg flex w-full cursor-pointer items-center justify-between gap-[12px] border-none bg-transparent py-[12px] font-[inherit]"
						>
							<span className="flex flex-col gap-[3px] text-left">
								<span className="text-[13.5px] font-medium">
									{t('calc.kybToggle.label')}
								</span>
								<span className="text-bg/72 font-mono text-[10px] tracking-[0.5px]">
									{t('calc.kybToggle.note')}
								</span>
							</span>
							<StateTag on={kyb} labelOn={stateOn} labelOff={stateOff} />
						</button>

						{/* Companies slider (visible when KYB enabled) */}
						{kyb && (
							<div className="mt-[16px]">
								<div className="flex items-baseline justify-between">
									<span className="text-bg/70 font-mono text-[11px] tracking-[1px]">
										{t('calc.companiesLabel')}
									</span>
									<span className="font-mono text-[16px] font-bold">
										{nf(companies)}
									</span>
								</div>
								<input
									type="range"
									aria-label={t('calc.companiesLabel')}
									min={CALC.kyb.companies.min}
									max={CALC.kyb.companies.max}
									step={CALC.kyb.companies.step}
									value={companies}
									onChange={(e) =>
										setCompanies(parseInt(e.target.value, 10) || 0)
									}
									className="mt-[12px] min-h-6 w-full cursor-pointer"
									style={{ accentColor: 'var(--color-bg)' }}
								/>
							</div>
						)}
					</div>

					{/* Add-ons */}
					<div className="border-bg/12 mt-[22px] border-t pt-[14px]">
						<div className="text-bg/72 mb-[6px] font-mono text-[10px] tracking-[1.5px]">
							{t('calc.addonsHeader')}
						</div>

						{addonDefs.map((a) => (
							<div key={a.key}>
								<button
									type="button"
									onClick={a.toggle}
									className="text-bg flex w-full cursor-pointer items-center justify-between gap-[12px] border-none bg-transparent py-[12px] font-[inherit]"
								>
									<span className="flex flex-col gap-[3px] text-left">
										<span className="text-[13px] font-medium">{a.label}</span>
										<span className="text-bg/72 font-mono text-[10px] tracking-[0.5px]">
											{a.note}
										</span>
									</span>
									<StateTag
										on={a.value}
										labelOn={stateOn}
										labelOff={stateOff}
									/>
								</button>

								{/* SMS route sub-tabs */}
								{a.key === 'smsOtp' && smsOtp && (
									<div className="mt-[10px] flex gap-[6px]">
										{(['ru', 'global'] as const).map((route) => (
											<button
												key={route}
												type="button"
												onClick={() => setSmsRoute(route)}
												className={`flex min-h-11 flex-1 cursor-pointer items-center justify-center px-[6px] py-[8px] font-mono text-[10px] tracking-[0.5px] transition-colors ${
													smsRoute === route
														? 'bg-bg text-fg border-bg border'
														: 'border-bg/20 text-bg/72 border bg-transparent'
												}`}
											>
												{smsRouteLabels[route]}
											</button>
										))}
									</div>
								)}
							</div>
						))}
					</div>

					{/* Annual toggle */}
					<div className="border-bg/12 mt-[8px] border-t pt-[16px]">
						<button
							type="button"
							onClick={() => setAnnual((v) => !v)}
							className="text-bg flex w-full cursor-pointer items-center justify-between gap-[12px] border-none bg-transparent py-[12px] font-[inherit]"
						>
							<span className="flex flex-col gap-[3px] text-left">
								<span className="text-[13px] font-medium">
									{t('calc.annual.label')}
								</span>
								<span className="text-bg/72 font-mono text-[10px] tracking-[0.5px]">
									{t('calc.annual.note')}
								</span>
							</span>
							<StateTag on={annual} labelOn={stateOn} labelOff={stateOff} />
						</button>
					</div>
				</div>

				{/* ── Estimate panel ────────────────────────────────────────── */}
				{/* Theme-following surface: light in light theme, dark in dark theme
				    (bg-surface + text tokens). The SceneCanvas renders theme-aware at
				    14%; border-border separates it from the page in either theme. */}
				<div className="border-border bg-surface text-fg relative overflow-hidden border">
					<SceneCanvas
						scene="kyc-05"
						className="absolute inset-0 h-full w-full opacity-[0.14]"
					/>

					{/* Top sweep line (uses existing global keyframe; fg-toned so it
					    reads in both themes) */}
					<div className="bg-fg/10 absolute inset-x-0 top-0 h-px overflow-hidden">
						<div className="onekyc-sweep bg-fg/70 absolute top-0 left-0 h-px w-[36%]" />
					</div>

					{/* Content */}
					<div className="relative z-10 flex h-full flex-col p-[clamp(22px,2.6vw,32px)]">
						{/* Header row */}
						<div className="text-muted flex justify-between font-mono text-[10.5px] tracking-[2px]">
							<span>{t('calc.estimate.header')}</span>
							<span>{estPlanCode}</span>
						</div>

						{/* Invoice rows */}
						<div className="mt-[22px] flex flex-col gap-[2px]">
							{invoiceRows.map((row, i) => (
								<div
									key={i}
									className="border-border flex items-baseline justify-between gap-[14px] border-b py-[11px]"
								>
									<span className="flex flex-col gap-[2px]">
										<span className="text-fg text-[13.5px]">{row.label}</span>
										<span className="text-muted font-mono text-[10px] tracking-[0.3px]">
											{row.sub}
										</span>
									</span>
									<span className="text-fg font-mono text-[14px] font-medium whitespace-nowrap">
										{row.val}
									</span>
								</div>
							))}
						</div>

						{/* Total block */}
						<div className="mt-auto pt-[22px]">
							<div className="flex items-end justify-between gap-[12px]">
								<span className="text-muted font-mono text-[11px] tracking-[1.5px]">
									{totalCaption}
								</span>
								<span className="text-fg font-mono text-[clamp(30px,4vw,44px)] leading-[1] font-bold tracking-[-0.02em]">
									{totalValue}
								</span>
							</div>

							{/* Annual lines */}
							{hasAnnual && (
								<>
									<div className="border-border text-muted mt-[10px] flex justify-between border-t pt-[10px] font-mono text-[11px] tracking-[0.5px]">
										<span>{t('calc.estimate.annualCaption')}</span>
										<span>{formatUsd(annualMonthly)}</span>
									</div>
									<div className="text-fg mt-[7px] flex justify-between font-mono text-[11px] tracking-[0.5px]">
										<span>{t('calc.estimate.annualSave')}</span>
										<span>{formatUsd(annualSave)}</span>
									</div>
								</>
							)}

							{/* One-time line */}
							{hasOneTime && (
								<div className="text-muted mt-[8px] flex justify-between font-mono text-[11px] tracking-[0.5px]">
									<span>{t('calc.estimate.oneTimeCaption')}</span>
									<span>{formatUsd(oneTime)}</span>
								</div>
							)}

							{/* CTA button — inverted (bg-fg / text-bg) so it contrasts with
							    the surface panel in both themes */}
							<button
								type="button"
								onClick={() => openAuth('register')}
								className="bg-fg text-bg mt-[18px] flex w-full cursor-pointer items-center justify-between border-none px-[18px] py-[15px] font-mono text-[12px] tracking-[1.5px] transition-opacity hover:opacity-85"
							>
								<span>{ctaText}</span>
								<span aria-hidden="true">-&gt;</span>
							</button>

							{/* Disclaimer */}
							<div className="text-muted mt-[12px] font-mono text-[9.5px] leading-[1.5] tracking-[0.5px]">
								{t('calc.estimate.disclaimer')}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
